import { InjectionToken } from '@angular/core';
import { IAddressBasicData } from '@df/session/api/address-book/address';

export namespace NDfModuleLoqate {
    export const DEFAULT_OPTIONS = {
        timeout: 15000,
        enabled: true
    };

    export const OPTIONS = new InjectionToken<IModuleOptions>('DF_MODULE_LOQATE_OPTIONS', {
        providedIn: 'root',
        factory: () => DEFAULT_OPTIONS
    });

    export interface IModuleOptions {
        addressKey?: string;
        emailKey?: string;
        phoneKey?: string;
        timeout: number;
        enabled: boolean;
    }

    export enum Endpoint {
        Address = 'https://api.addressy.com/Cleansing/International/Batch/v1.00/json4.ws',
        Email = 'https://api.addressy.com/EmailValidation/Interactive/Validate/v2.00/json3.ws',
        Phone = 'https://api.addressy.com/PhoneNumberValidation/Interactive/Validate/v2.2/json3.ws'
    }

    export enum Events {
        EmailResultConverted = 'loqateEmailConverted',
        EmailValidation = 'loqateEmailValidation',
        PhoneResultConverted = 'loqatePhoneConverted',
        PhoneValidation = 'loqatePhoneValidation'
    }

    // @see https://www.loqate.com/developers/apis/cleanse-api/international-batch-cleanse/#request
    export interface AddressValidationRequest {
        Key: string;
        Geocode?: boolean;
        Addresses: Address[];
        Options?: any; // not set yet
    }

    export interface AddressValidationResponse {}

    export interface EmailValidationRequest {
        Key: string;
        Email: string;
        Timeout?: string;
    }

    export interface EmailValidationResponse {
        Items: EmailValidationItem[];
    }

    export interface EmailValidationItem extends ValidationItemError {
        // Valid - The email address has been fully validated (including the account portion)
        // Valid_CatchAll - The domain has been validated but the account could not be validated
        // Invalid - The email address is invalid and shouldn't be accepted
        // Timeout - The validation could not be completed within the timeout specified (try increasing the timeout value)
        ResponseCode: 'Valid' | 'Valid_CatchAll' | 'Invalid' | 'Timeout';
        // A textual description of the ResponseCode returned
        ResponseMessage: string;
        // The email address that verification was attempted on.
        EmailAddress: string;
        // The account portion of the email address provided.
        UserAccount: string;
        // The domain portion of the email address provided.
        Domain: string;
        // Whether the email address provided is a disposable mailbox
        // (some companies create temporary mailboxes which shouldn't be used for marketing communications).
        IsDisposableOrTemporary: boolean;
        // This field is no longer valid and will always return False
        IsComplainerOrFraudRisk: boolean;
        // The duration (in seconds) that the email validation took (maximum timeout enforced at 15 seconds).
        // We recommend a high timeout (at least 5 seconds) value as it will minimize the number of "Timeout" responses returned.
        Duration: number;
    }

    export interface PhoneValidationRequest {
        Key: string;
        Phone: string;
        Country?: string;
        Iso3Country?: string;
        AdditionalFields?: string;
    }

    export interface PhoneValidationResponse {
        Items: PhoneValidationItem[];
    }

    export interface PhoneValidationItem extends ValidationItemError {
        // The recipient phone number in international format
        PhoneNumber: string;
        // Whether the number is valid or not (Maybe is returned if full validation couldn't be performed.
        // Cellular network issues can cause temporary failures in the validation procedure).
        IsValid: 'Yes' | 'No' | 'Maybe';
        // The domestic network format (useful for dialling from within the same country)
        NationalFormat: string;
        // The country prefix that must be prepended to the number when dialling internationally
        CountryPrefix: string;
    }

    export interface ValidationItemError {
        // Error ID @see https://www.loqate.com/resources/support/apis/generic-errors/
        Error?: string;
        Description?: string;
        Cause?: string;
        Resolution?: string;
    }

    export interface Address {
        // This field holds the full address, correctly formatted for mailing in the relevant country, including line breaks specified using the AddressLineSeparator option.
        Address?: string;
        // These fields can be used to specify input address line data, and on output will contain the correctly formatted address split into individual address lines for mailing in the relevant country.
        Address1?: string;
        Address2?: string;
        Address3?: string;
        Address4?: string;
        Address5?: string;
        Address6?: string;
        Address7?: string;
        Address8?: string;
        // These fields contain the individual lines contained within the DeliveryAddress field.
        DeliveryAddress1?: string;
        DeliveryAddress2?: string;
        // Optional. This field will be passed through to the output unchanged.
        ID?: string;
        // field is used to supply the country name or code (ISO 3166 2-character country code and ISO 3166 3-character country code).
        Country?: string;
        // This field holds the largest geographic data element within a country.
        SuperAdministrativeArea?: string;
        // This field holds the most common geographic data element within a country. For instance, USA State, and Canadian Province.
        AdministrativeArea?: string;
        // This field holds the smallest geographic data element within a country. For instance, USA County.
        SubAdministrativeArea?: string;
        // This field holds the most common population center data element within a country. For instance, USA City, Canadian Municipality.
        Locality?: string;
        // This field holds a smaller population center data element, dependent on the contents of the Locality field. For instance, Turkish Neighborhood.
        DependentLocality?: string;
        // This field holds the smallest population center data element, dependent on both the contents of the Locality and DependentLocality fields. For instance, UK Village.
        DoubleDependentLocality?: string;
        // This field holds the most common street or block data element within a country. For instance, USA Street.
        Thoroughfare?: string;
        // This field holds the dependent street or block data element within a country. For instance, UK Dependent Street.
        DependentThoroughfare?: string;
        // This field contains the descriptive name identifying an individual location, should one exist.
        Building?: string;
        // This field contains the alphanumeric code identifying an individual location, should one exist.
        Premise?: string;
        // This field contains the secondary identifiers for a particular delivery point. For instance, “FLAT 1” or “SUITE 212”.
        SubBuilding?: string;
        // This field contains the parsed floor information within the SubBuilding field, and includes the floor label (or type) and a numeric where applicable. For example, “L7” or “Fl 12”.
        SubBuildingFloor?: string;
        // This field contains the complete postal code for a particular delivery point, should such detail be able to be determined.
        PostalCode?: string;
        // field contains the primary postal code used for a particular country. For instance, USA Zip, Canadian Postcode, Indian PINcode.
        PostalCodePrimary?: string;
        // field contains secondary postal code information, if used in a particular country and if such detail is able to be determined and reference data is available. For instance, USA Zip Plus 4.
        PostalCodeSecondary?: string;
        // This field contains the business name associated with a particular delivery point, should one exist.
        Organization?: string;
        // This field contains the post box for a particular delivery point, should one exist.
        PostBox?: string;
    }

    export interface MatchedAddress extends Address {
        // Address Quality Index
        // @see https://support.loqate.com/documentation/reportcodes/address-quality-index/
        AQI: 'A' | 'B' | 'C' | 'D' | 'E';
        // Address Verification Code
        // @see https://support.loqate.com/documentation/reportcodes/address-verification-code/
        AVC: 'string';
    }

    export interface AddressValidationResult {
        matches: IAddressBasicData[];
        valid: boolean;
        hasSuggestion: boolean;
    }
}
